/**
 * Opening a conversation the history list already knows is lane v2.
 *
 * The list row carries `source: "v2"` (the server pinned it); the route would otherwise learn that only after an async
 * R0 + R1 probe, and in that window `showsLaneV2` is false so Send fell through to the old lane (`POST /api/runs`,
 * 409 WRONG_LANE). This module records the row's source so the composer can say "resolving" instead; the route attaches without the R1 gate.
 */
import { invalidateCapabilities, resolveCapabilities } from "./lane";
import { useLaneV2SessionStore } from "@/stores/laneV2SessionStore";
import { useWeaveSessionStore } from "@/stores/weaveSessionStore";
import { clearComposerInputs } from "../openConversation";
import { resolveActiveOrgId, useAuthStoreV1 } from "@/stores/authStore_v1";

const v2Sourced = new Set<string>();

export function markV2Sourced(sessionId: string): void {
  v2Sourced.add(sessionId);
}

export function isV2Sourced(sessionId: string | null | undefined): boolean {
  return !!sessionId && v2Sourced.has(sessionId);
}

export function clearV2SourcedForTests(): void {
  v2Sourced.clear();
}

function activeOrgId(): string | null {
  return resolveActiveOrgId(useAuthStoreV1.getState());
}

const inFlight = new Map<string, Promise<void>>();

/**
 * The one place a v2-sourced (history-opened) conversation is attached (the non-sourced R0 probe branch in useLaneV2Route attaches on its own) (the route effect and the WRONG_LANE recovery both call it, nobody
 * calls `store.attach` for a v2-sourced id on their own). Concurrent callers share one attach, so a second attach can
 * never bump the store's generation under an in-flight send. A cached LEGACY capabilities answer is not believed for a
 * session the server pinned to v2: it is evicted and R0 asked again once, so a single failed probe cannot strip the
 * chip, Save and the deploy mode. Failures surface in the store's own `error` (attach catches).
 */
export function ensureV2Attached(sessionId: string): Promise<void> {
  markV2Sourced(sessionId);
  const store = useLaneV2SessionStore.getState();
  if (store.sessionId === sessionId && !store.draft && store.streamStatus !== "idle") return Promise.resolve();
  const running = inFlight.get(sessionId);
  if (running) return running;
  const orgId = activeOrgId();
  const p = (async () => {
    let caps = await resolveCapabilities(orgId);
    if (!caps.readModel) {
      invalidateCapabilities(orgId);
      caps = await resolveCapabilities(orgId);
    }
    await useLaneV2SessionStore.getState().attach(sessionId, {
      deployMode: caps.deployMode,
      turnAnswersAsk: caps.turnAnswersAsk,
      offline: caps.offline,
      turnAttachments: caps.turnAttachments,
      turnIdeState: caps.turnIdeState,
      branchSave: caps.branchSave,
      liveFrames: caps.liveFrames,
    });
  })().finally(() => inFlight.delete(sessionId));
  inFlight.set(sessionId, p);
  return p;
}

/**
 * History / sidebar open of a v2 row: adopt it as the active conversation and record that the server pinned it to v2.
 * The route effect (the attach owner) sees the hint, skips the R1 gate and calls `ensureV2Attached`.
 */
export function openV2Session(sessionId: string): void {
  markV2Sourced(sessionId);
  // Switching to another chat: the composer's files and chip belong to the one being left. (Not on the draft pin,
  // which adopts mid first send and must not wipe the files being sent.)
  if (useWeaveSessionStore.getState().sessionId !== sessionId) clearComposerInputs();
  useWeaveSessionStore.getState().adopt(sessionId);
}

/**
 * A 409 WRONG_LANE on the old lane means the conversation is v2: attach it (once) and hand the text to `send`, which
 * must be the composer's own send so the chip, paperclips, ide_state and clear-on-sent tracking apply exactly as on any
 * other lane-v2 send. Returns false when the turn could not be started (the caller keeps the text and says so).
 * It never starts a new conversation.
 */
export async function resendOnLaneV2(sessionId: string, send: () => Promise<boolean>): Promise<boolean> {
  await ensureV2Attached(sessionId);
  if (useLaneV2SessionStore.getState().sessionId !== sessionId) return false;
  return send();
}
